import React, { useState, useEffect } from "react";
import { Card, ListGroup, Dropdown, Spinner } from "react-bootstrap";
import styles from "./Card.module.css";
import GenericFHIRViewerModal from "../modals/ResourceViewerModal";
import { formatLocalDate } from "../utils/dateUtils";
import StethoscopeIcon from "../assets/stethoscope_96.png";

function EncounterCard({
  title,
  inContext,
  encounterData,
  fhirClient,
  patientId,
  enableSearch = false,
  onEncounterChange,
  isReply
}) {
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalEncounterDetails, setModalEncounterDetails] = useState(null);
  const [serviceProviderName, setServiceProviderName] = useState("Loading...");
  const [encounterHistory, setEncounterHistory] = useState([]);
  const [selectedEncounter, setSelectedEncounter] = useState(encounterData || null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const handleModalClose = () => setShowModal(false);

  // 🔄 Sync encounterData into selectedEncounter
  useEffect(() => {
    if (
      encounterData &&
      typeof encounterData === "object" &&
      "error" in encounterData &&
      typeof encounterData.error === "number"
    ) {
      if (encounterData.error === 410) {
        setError("This encounter has been removed by the system.");
      } else {
        setError(`Error loading encounter (code: ${encounterData.error}).`);
      }
      setSelectedEncounter(null);
    } else {
      setError("");
      setSelectedEncounter(encounterData || null);
    }
  }, [encounterData]);

  // 🏥 Fetch service provider name
  useEffect(() => {
    const fetchServiceProvider = async () => {
      const ref = selectedEncounter?.serviceProvider?.reference;
      if (!fhirClient || !ref || !ref.includes("/") || ref.endsWith("/undefined")) {
        setServiceProviderName("N/A");
        return;
      }
      try {
        const data = await fhirClient.request(ref);
        setServiceProviderName(data?.name || "Unknown Organization");
      } catch (err) {
        console.error("Failed to fetch service provider details", err);
        setServiceProviderName("N/A");
      }
    };

    if (selectedEncounter) {
      fetchServiceProvider();
    } else {
      setServiceProviderName("N/A");
    }
  }, [selectedEncounter, fhirClient]);

  // 📜 Fetch encounter history
  useEffect(() => {
    if (!enableSearch || !patientId || !fhirClient) return;

    const fetchEncountersForPatient = async () => {
      setHistoryLoading(true);
      try {
        const response = await fhirClient.request(
          `Encounter?patient=Patient/${patientId}&_sort=-date&_count=10`
        );
        const found = response.entry?.map((e) => e.resource) || [];
        setEncounterHistory(found);
      } catch (err) {
        console.error("Error fetching encounter history:", err);
        setEncounterHistory([]);
      } finally {
        setHistoryLoading(false);
      }
    };

    fetchEncountersForPatient();
  }, [enableSearch, patientId, fhirClient]);

  useEffect(() => {
    if (!isReply && enableSearch) {
      if (encounterHistory.length > 0) {
        const firstEncounter = encounterHistory[0];
        setSelectedEncounter(firstEncounter);
        if (onEncounterChange) {
          onEncounterChange(firstEncounter);
        }
      } else {
        setSelectedEncounter(null);
        if (onEncounterChange) {
          onEncounterChange(null);
        }
      }
    }
  }, [encounterHistory, patientId]);

  // 🔍 Modal viewer
  const handleModalOpen = async () => {
    try {
      const data = await fhirClient.request(`Encounter/${selectedEncounter.id}`);
      setModalEncounterDetails(data);
      setShowModal(true);
    } catch (err) {
      console.error("Failed to fetch encounter details", err);
      setModalEncounterDetails(null);
      setShowModal(true);
    }
  };

  // 🔼 Encounter selection
  const handleSelectEncounter = (enc) => {
    setSelectedEncounter(enc);
    if (onEncounterChange) {
      onEncounterChange(enc);
    }
  };

  return (
    <>
      <Card className={styles.providerCard}>
        <Card.Header
          className={`d-flex justify-content-between align-items-center ${inContext ? styles.inContextCardHeader : styles.cardHeader}`}
          onClick={handleModalOpen}
          style={{ cursor: "pointer" }}
        >
          <h6 className="mb-0"><strong>{title}</strong></h6>

          <div className="d-flex align-items-center" onClick={(e) => e.stopPropagation()}>
            {enableSearch && !isReply && (
              historyLoading ? (
                <Spinner animation="border" role="status" size="sm" className="me-2">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              ) : (
                <Dropdown
                  onSelect={(eventKey) =>
                    handleSelectEncounter(encounterHistory.find((e) => e.id === eventKey))
                  }
                >
                  <Dropdown.Toggle variant="outline-primary" size="sm">
                    Select Encounter
                  </Dropdown.Toggle>
                  <Dropdown.Menu className={styles.dropdownScroll}>
                    {encounterHistory.map((enc) => (
                      <Dropdown.Item key={enc.id} eventKey={enc.id}>
                        {enc.type?.[0]?.text || "Encounter"} · {formatLocalDate(enc.period?.start)}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              )
            )}

            <span>
              <img
                src={StethoscopeIcon}
                alt="Encounter Icon"
                className={styles.fhirIcon}
                onClick={(e) => {
                  e.stopPropagation();   // prevent header click
                  handleModalOpen();
                }}
              />
            </span>
          </div>
        </Card.Header>


        <Card.Body>
          {error ? (
            <p className={styles.errorText}>{error}</p>
          ) : (
            <ListGroup variant="flush">
              <ListGroup.Item className={styles.listItem}><strong>Type:</strong> {selectedEncounter?.type?.[0]?.text || "N/A"}</ListGroup.Item>
              <ListGroup.Item className={styles.listItem}><strong>Status:</strong> {selectedEncounter?.status || "N/A"}</ListGroup.Item>
              <ListGroup.Item className={styles.listItem}><strong>Class:</strong> {selectedEncounter?.class?.code || "N/A"}</ListGroup.Item>
              <ListGroup.Item className={styles.listItem}><strong>Service Provider:</strong> {serviceProviderName}</ListGroup.Item>
              <ListGroup.Item className={styles.listItem}><strong>Start Date:</strong> {formatLocalDate(selectedEncounter?.period?.start)}</ListGroup.Item>
              <ListGroup.Item className={styles.listItem}><strong>End Date:</strong> {selectedEncounter?.period?.end ? formatLocalDate(selectedEncounter?.period?.end) : "Ongoing"}</ListGroup.Item>
              <ListGroup.Item className={styles.listItem}><strong>Reason:</strong> {selectedEncounter?.reasonCode?.map(rc => rc.coding?.map(code => code.display).filter(Boolean).join("; ")).filter(Boolean).join("; ") || "N/A"}</ListGroup.Item>
            </ListGroup>
          )}
        </Card.Body>
      </Card>

      <GenericFHIRViewerModal
        show={showModal}
        onClose={handleModalClose}
        resource={modalEncounterDetails}
        //title={selectedEncounter?.type?.[0]?.text || "N/A"}
        fhirClient={fhirClient}
        //icon={StethoscopeIcon}
      />
    </>
  );
}

export default EncounterCard;